import { Dispatch, SetStateAction, useState } from 'react'
import { Image as ImageIcon, Tag } from 'lucide-react'
import type { EventType, EventAttributes } from '@fellowship/shared'
import { EVENT_TYPES } from '../ui/eventTypes'
import { ImageUpload } from '../admin/ImageUpload'
import { EventImagePicker } from '../ui/EventImagePicker'
import { Field, ModuleToggle } from './EventFormPrimitives'
import { DishGoalsFields } from './DishGoalsFields'
import { OutdoorDetailsFields } from './OutdoorDetailsFields'
import { AttributeCheckboxGrid } from './AttributeCheckboxGrid'

// The "More details" module-toggle cluster (dish coordination, outdoor details,
// study details, cover photo, attributes). Shared by the create and edit person-event flows.

export function MoreDetailsFields({
  eventType,
  attributes,
  setAttributes,
  mainGoal, setMainGoal,
  saladGoal, setSaladGoal,
  sideGoal, setSideGoal,
  dessertGoal, setDessertGoal,
  drinkGoal, setDrinkGoal,
  outdoorDifficulty, setOutdoorDifficulty,
  outdoorDistance, setOutdoorDistance,
  outdoorElevation, setOutdoorElevation,
  outdoorMeetupNote, setOutdoorMeetupNote,
  studyTopic, setStudyTopic,
  studyScripture, setStudyScripture,
  imageUrl, setImageUrl,
}: {
  eventType: EventType
  attributes: EventAttributes
  setAttributes: Dispatch<SetStateAction<EventAttributes>>
  mainGoal: number; setMainGoal: (v: number) => void
  saladGoal: number; setSaladGoal: (v: number) => void
  sideGoal: number; setSideGoal: (v: number) => void
  dessertGoal: number; setDessertGoal: (v: number) => void
  drinkGoal: number; setDrinkGoal: (v: number) => void
  outdoorDifficulty: 'easy' | 'moderate' | 'strenuous'; setOutdoorDifficulty: (v: 'easy' | 'moderate' | 'strenuous') => void
  outdoorDistance: string; setOutdoorDistance: (v: string) => void
  outdoorElevation: string; setOutdoorElevation: (v: string) => void
  outdoorMeetupNote: string; setOutdoorMeetupNote: (v: string) => void
  studyTopic: string; setStudyTopic: (v: string) => void
  studyScripture: string; setStudyScripture: (v: string) => void
  imageUrl: string | null; setImageUrl: (v: string | null) => void
}) {
  const [coverPhotoEnabled, setCoverPhotoEnabled] = useState(false)
  const [attrOpen, setAttrOpen] = useState(true)
  const coverPhotoOn = coverPhotoEnabled || imageUrl !== null

  const potluckType = EVENT_TYPES.potluck
  const outdoorsType = EVENT_TYPES.outdoors
  const studyType = EVENT_TYPES.study

  return (
    <div className="flex flex-col gap-2">
      <ModuleToggle
        icon={<potluckType.icon size={16} />}
        iconBg={potluckType.soft}
        iconColor={potluckType.color}
        label="Dish coordination"
        suggested={eventType === 'potluck'}
        on={Boolean(attributes.bringADish)}
        onToggle={() => setAttributes((p) => ({ ...p, bringADish: p.bringADish ? undefined : true }))}
      >
        <div>
          <div className="text-[12px] font-bold text-ink-2 tracking-[0.04em] uppercase mb-3">Dish goals</div>
          <DishGoalsFields
            mainGoal={mainGoal} setMainGoal={setMainGoal}
            saladGoal={saladGoal} setSaladGoal={setSaladGoal}
            sideGoal={sideGoal} setSideGoal={setSideGoal}
            dessertGoal={dessertGoal} setDessertGoal={setDessertGoal}
            drinkGoal={drinkGoal} setDrinkGoal={setDrinkGoal}
          />
        </div>
      </ModuleToggle>

      <ModuleToggle
        icon={<outdoorsType.icon size={16} />}
        iconBg={outdoorsType.soft}
        iconColor={outdoorsType.color}
        label="Outdoor details"
        suggested={eventType === 'outdoors'}
        on={Boolean(attributes.outdoorActivity)}
        onToggle={() => setAttributes((p) => ({ ...p, outdoorActivity: p.outdoorActivity ? undefined : true }))}
      >
        <OutdoorDetailsFields
          difficulty={outdoorDifficulty} setDifficulty={setOutdoorDifficulty}
          distance={outdoorDistance} setDistance={setOutdoorDistance}
          elevation={outdoorElevation} setElevation={setOutdoorElevation}
          meetupNote={outdoorMeetupNote} setMeetupNote={setOutdoorMeetupNote}
        />
      </ModuleToggle>

      {eventType === 'study' && (
        <ModuleToggle
          icon={<studyType.icon size={16} />}
          iconBg={studyType.soft}
          iconColor={studyType.color}
          label="Study details"
          suggested
          on
          onToggle={() => {/* auto-enabled for study type */}}
        >
          <div className="flex flex-col gap-3.5">
            <Field label="Topic" htmlFor="studyTopic">
              <input
                id="studyTopic"
                type="text"
                value={studyTopic}
                onChange={(e) => setStudyTopic(e.target.value)}
                placeholder="The Sermon on the Mount"
                className="input"
              />
            </Field>
            <Field label="Scripture reference" htmlFor="studyScripture">
              <input
                id="studyScripture"
                type="text"
                value={studyScripture}
                onChange={(e) => setStudyScripture(e.target.value)}
                placeholder="Matthew 5–7"
                className="input"
              />
            </Field>
          </div>
        </ModuleToggle>
      )}

      <ModuleToggle
        icon={<ImageIcon size={16} />}
        label="Cover photo"
        on={coverPhotoOn}
        onToggle={() => {
          if (coverPhotoOn) { setImageUrl(null); setCoverPhotoEnabled(false) }
          else setCoverPhotoEnabled(true)
        }}
      >
        <div className="flex flex-col gap-3">
          {imageUrl && (
            <div className="rounded-xl overflow-hidden aspect-[3/2]">
              <img src={imageUrl} alt="" className="w-full h-full object-cover block" />
            </div>
          )}
          <div>
            <div className="text-[12px] text-muted mb-2">
              {imageUrl ? 'Or choose a different stock photo' : 'Choose one of our stock photos, or upload your own'}
            </div>
            <EventImagePicker eventType={eventType} value={imageUrl} onChange={setImageUrl} />
          </div>
          <ImageUpload context="event-image" onUpload={setImageUrl}>
            <span className="text-[13px] text-primary font-medium cursor-pointer font-sans">
              {imageUrl ? 'Upload a different photo' : 'Upload your own photo'}
            </span>
          </ImageUpload>
        </div>
      </ModuleToggle>

      <ModuleToggle
        icon={<Tag size={16} />}
        label="Attributes"
        on={attrOpen}
        onToggle={() => setAttrOpen((p) => !p)}
      >
        <AttributeCheckboxGrid
          attributes={attributes}
          onToggle={(slug, checked) =>
            setAttributes((prev) => ({ ...prev, [slug]: checked || undefined }))
          }
        />
      </ModuleToggle>
    </div>
  )
}
